import { DeviceType, PhysicalDeviceType } from '../../../../shared/models/device';
import { CSVTemplateGenerator, CSVTemplateOptions } from './csv-template-generator.util';
import { BacnetPoint } from '../../../../shared/models/point';
import { ModbusPoint, ModbusPointID, ModbusDataType } from '../../../../shared/models/modbus';
import { LoRaWANPoint, makeLoRaWANPointID } from '../../../../shared/models/lorawan';
import { CSVTileRow } from '../../shared/models/csv-tile';
import {
  VALID_BACNET_POINT_TYPES,
  VALID_MODBUS_DATA_TYPES,
  VALID_MODBUS_BYTE_ORDER,
  VALID_LORAWAN_POINT_TYPES,
  VALID_LORAWAN_POINT_FORMATS,
  isLoRaWANPointType,
  isLoRaWANPointFormat
} from '../../shared/constants/csv-validation.constants';
import { CsvFieldId } from '../../shared/constants/csv-columns.constants';
import { ProtocolPropertyLabel } from '../../../../shared/constants/protocol-property-label.constants';

export interface BulkCreationProtocolHandler {
  generateNumericTemplate(options: Omit<CSVTemplateOptions, 'protocol'>): any[];
  generateToggleTemplate(options: Omit<CSVTemplateOptions, 'protocol'>): any[];
  getNumericTemplateFilename(hasPoints: boolean): string;
  getToggleTemplateFilename(hasPoints: boolean): string;
  generateTileName(point: BacnetPoint | ModbusPoint | LoRaWANPoint): string;
  parseAndValidatePointConfig(row: CSVTileRow): { pointId: string | null, errors: string[] }; // Parse, validate, and construct Point ID
}

class ProtocolHandler implements BulkCreationProtocolHandler {
  constructor(private protocol: DeviceType) {}

  generateNumericTemplate(options: Omit<CSVTemplateOptions, 'protocol'>): any[] {
    return CSVTemplateGenerator.generateNumericTemplate({
      ...options,
      protocol: this.protocol
    });
  }

  generateToggleTemplate(options: Omit<CSVTemplateOptions, 'protocol'>): any[] {
    return CSVTemplateGenerator.generateToggleTemplate({
      ...options,
      protocol: this.protocol
    });
  }

  getNumericTemplateFilename(hasPoints: boolean): string {
    const suffix = hasPoints ? 'with-points' : 'template';
    return `${this.protocol.toLowerCase()}-numeric-tiles-${suffix}.csv`;
  }

  getToggleTemplateFilename(hasPoints: boolean): string {
    const suffix = hasPoints ? 'with-points' : 'template';
    return `${this.protocol.toLowerCase()}-toggle-tiles-${suffix}.csv`;
  }

  generateTileName(point: BacnetPoint | ModbusPoint | LoRaWANPoint): string {
    // Return the point name if it exists and is not empty
    if (point.Name && point.Name.trim()) {
      return point.Name;
    }

    // Protocol-specific fallback logic
    switch (this.protocol) {
      case DeviceType.BACnet:
        if ('Address' in point && 'Type' in point) {
          // For BACnet, try Description first, then use BACnet Object Type + Instance Number
          if (point.Description && point.Description.trim()) {
            return point.Description;
          }
          return `${point.Type} ${point.Address}`;
        }
        break;

      case DeviceType.Modbus:
        if ('ID' in point) {
          try {
            const configId = new ModbusPointID(point.ID);
            return `Modbus Point ${configId.Address}`;
          } catch {
            // Fall through to default if ID parsing fails
          }
        }
        break;

      case DeviceType.LoRaWAN:
        // For LoRaWAN, Name is always present in the interface
        // This is a fallback in case it's empty
        return 'Untitled LoRaWAN Point';
    }

    // Default fallback
    return 'Untitled Tile';
  }

  parseAndValidatePointConfig(row: CSVTileRow): { pointId: string | null, errors: string[] } {
    switch (this.protocol) {
      case DeviceType.BACnet:
        return this.parseAndValidatePointConfigBACnet(row);
      case DeviceType.Modbus:
        return this.parseAndValidatePointConfigModbus(row);
      case DeviceType.LoRaWAN:
        return this.parseAndValidatePointConfigLoRaWAN(row);
    }
  }

  private parseAndValidatePointConfigBACnet(row: CSVTileRow): { pointId: string | null, errors: string[] } {
    const errors: string[] = [];

    // Parse BACnet Instance Number
    const instanceNumberStr = row[CsvFieldId.BACNET_INSTANCE_NUMBER];
    if (!instanceNumberStr) {
      errors.push(`${ProtocolPropertyLabel.BACNET_INSTANCE_NUMBER} is required for BACnet tiles`);
      return { pointId: null, errors };
    }

    // Validate that instance number is numeric, but keep it as string for PointID
    const instanceNumber = parseInt(instanceNumberStr, 10);
    if (isNaN(instanceNumber)) {
      errors.push(`Cannot parse BACnet Instance Number: "${instanceNumberStr}". Must be a valid number`);
      return { pointId: null, errors };
    }

    // Parse and validate BACnet Object Type
    const objectType = row[CsvFieldId.BACNET_OBJECT_TYPE];
    if (!objectType) {
      errors.push(`${ProtocolPropertyLabel.BACNET_OBJECT_TYPE} is required for BACnet tiles`);
      return { pointId: null, errors };
    }

    if (!VALID_BACNET_POINT_TYPES.includes(objectType as any)) {
      errors.push(`Invalid BACnet Object Type: "${objectType}". Must be one of: ${VALID_BACNET_POINT_TYPES.join(', ')}`);
      return { pointId: null, errors };
    }

    // Construct Point ID from object type and instance number (keep instance as string to match BacnetPoint.Address type)
    const constructedPointId = JSON.stringify([objectType, instanceNumberStr]);
    return { pointId: constructedPointId, errors: [] };
  }

  private parseAndValidatePointConfigModbus(row: CSVTileRow): { pointId: string | null, errors: string[] } {
    const errors: string[] = [];

    // Parse Modbus Address (register address)
    const pointIdStr = row[CsvFieldId.MODBUS_ADDRESS];
    if (!pointIdStr) {
      errors.push(`${ProtocolPropertyLabel.MODBUS_ADDRESS} is required for Modbus tiles`);
      return { pointId: null, errors };
    }

    const address = parseInt(pointIdStr, 10);
    if (isNaN(address)) {
      errors.push(`Cannot parse Modbus Address: "${pointIdStr}". Must be a valid number`);
      return { pointId: null, errors };
    }

    if (address < 0) {
      errors.push(`Invalid register address: ${address}. Must be non-negative`);
      return { pointId: null, errors };
    }

    // Parse and validate Modbus Data Type
    const dataType = row[CsvFieldId.MODBUS_DATA_TYPE];
    if (!dataType) {
      errors.push(`${ProtocolPropertyLabel.MODBUS_DATA_TYPE} is required for Modbus tiles`);
      return { pointId: null, errors };
    }

    if (!VALID_MODBUS_DATA_TYPES.includes(dataType as any)) {
      errors.push(`Invalid Modbus Data Type: "${dataType}". Must be one of: ${VALID_MODBUS_DATA_TYPES.join(', ')}`);
      return { pointId: null, errors };
    }

    // Parse and validate Modbus Byte Order (except for bool)
    const byteOrder = row[CsvFieldId.MODBUS_BYTE_ORDER] || '';
    if (dataType !== ModbusDataType.Bool) {
      if (!byteOrder) {
        errors.push(`${ProtocolPropertyLabel.MODBUS_BYTE_ORDER} is required for non-bool Modbus tiles`);
        return { pointId: null, errors };
      }

      if (!VALID_MODBUS_BYTE_ORDER.includes(byteOrder as any)) {
        errors.push(`Invalid Modbus Byte Order: "${byteOrder}". Must be one of: ${VALID_MODBUS_BYTE_ORDER.join(', ')}`);
        return { pointId: null, errors };
      }
    }

    // Parse Modbus Functions (writable flag, optional, defaults to false)
    let writable = false;
    const modbusFunc = row[CsvFieldId.MODBUS_FUNCTIONS];
    if (modbusFunc) {
      const lower = modbusFunc.toLowerCase();
      if (lower === 'true') {
        writable = true;
      } else if (lower === 'false') {
        writable = false;
      } else {
        errors.push(`Cannot parse Modbus Functions: "${modbusFunc}". Must be "true" or "false" (case-insensitive)`);
        return { pointId: null, errors };
      }
    }

    // Construct Point ID using ModbusConfigID
    const configId = new ModbusPointID();
    configId.Type = dataType as ModbusDataType;
    configId.Address = address;
    configId.Endianness = byteOrder as any;
    configId.Writable = writable;

    const constructedPointId = configId.serialize();
    return { pointId: constructedPointId, errors: [] };
  }

  private parseAndValidatePointConfigLoRaWAN(row: CSVTileRow): { pointId: string | null, errors: string[] } {
    const errors: string[] = [];

    // Parse LoRaWAN Point Name
    const pointName = row[CsvFieldId.LORAWAN_POINT_NAME];
    if (!pointName || !pointName.trim()) {
      errors.push(`${ProtocolPropertyLabel.LORAWAN_POINT_NAME} is required for LoRaWAN tiles`);
      return { pointId: null, errors };
    }

    // Parse and validate LoRaWAN Data Source (UP or UM)
    const pointType = row[CsvFieldId.LORAWAN_DATA_SOURCE];
    if (!pointType) {
      errors.push(`${ProtocolPropertyLabel.LORAWAN_DATA_SOURCE} is required for LoRaWAN tiles`);
      return { pointId: null, errors };
    }

    if (!isLoRaWANPointType(pointType)) {
      errors.push(`Invalid LoRaWAN Data Source: "${pointType}". Must be one of: ${VALID_LORAWAN_POINT_TYPES.join(', ')}`);
      return { pointId: null, errors };
    }

    // Parse and validate LoRaWAN Data Type (num or bool)
    const pointFormat = row[CsvFieldId.LORAWAN_DATA_TYPE];
    if (!pointFormat) {
      errors.push(`${ProtocolPropertyLabel.LORAWAN_DATA_TYPE} is required for LoRaWAN tiles`);
      return { pointId: null, errors };
    }

    if (!isLoRaWANPointFormat(pointFormat)) {
      errors.push(`Invalid LoRaWAN Data Type: "${pointFormat}". Must be one of: ${VALID_LORAWAN_POINT_FORMATS.join(', ')}`);
      return { pointId: null, errors };
    }

    // Construct Point ID using helper function
    const constructedPointId = makeLoRaWANPointID(pointType, pointName, pointFormat);
    return { pointId: constructedPointId, errors: [] };
  }
}

export const BULK_CREATION_PROTOCOL_HANDLERS: Record<PhysicalDeviceType, BulkCreationProtocolHandler> = {
  [DeviceType.BACnet]: new ProtocolHandler(DeviceType.BACnet),
  [DeviceType.Modbus]: new ProtocolHandler(DeviceType.Modbus),
  [DeviceType.LoRaWAN]: new ProtocolHandler(DeviceType.LoRaWAN),
};
